import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import './ComboBox.scss';

/**
 * ComboBox
 * @module
 * @param {array} data - All data for options
 * @param {string} textField - Text field
 * @param {object} [value] - Current value
 * @param {func} onChange - Function invoked on value change
 * @param {func} [onOpen] - Function invoked on dropdown list open
 * @param {bool} [disabled] - Is input readonly?
 * @param {bool} [required] - Is input required?
 * @param {string} name - Name
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {number} [tabIndex] - Tabindex
 * @param {string} [className] - Class name for dropdown list
 * @param {func} [onBlur] - Function invoked on dropdown list blur
 * @param {object} [popupSettings] - Popup settings
 * @param {object} [defaultItem] - Default item
 */
const ComboBoxComponent = (props) => {
  const { data, textField, value, onChange, onOpen, suggest, filterable, required, name, validationMessage, disabled, tabIndex, className, onBlur, popupSettings, defaultItem } = props;
  const [filteredData, setFilteredData] = useState(data);
  const comboBoxRef = useRef(null);

  const onFilter = (event) => {
    setFilteredData(filterBy(data, event.filter));
  };

  const handleChange = (event) => {
    if (event.value == null) {
      comboBoxRef.current.focus();
    }
    onChange(name, event.value);
  };

  //tests
  useEffect(() => {
    if (comboBoxRef.current && comboBoxRef.current.element && props.name) {
      comboBoxRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name])

  return (
    <ComboBox
      ref={comboBoxRef}
      className={`combobox ${className || ''} ${disabled ? 'disabled-field' : ''}`}
      data={filteredData}
      textField={textField}
      dataItemKey={textField}
      value={value}
      onChange={handleChange}
      suggest={suggest}
      filterable={filterable ?? true}
      onFilterChange={onFilter}
      required={required}
      validationMessage={validationMessage}
      disabled={disabled}
      tabIndex={tabIndex}
      onOpen={onOpen}
      onBlur={onBlur}
      popupSettings={popupSettings}
      defaultItem={defaultItem}
    />
  );
};

ComboBoxComponent.propTypes = {
  data: PropTypes.array.isRequired,
  textField: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  suggest: PropTypes.bool,
  filterable: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  validationMessage: PropTypes.string,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  popupSettings: PropTypes.object,
  defaultItem: PropTypes.object
}

export default ComboBoxComponent;